import {
	getIsHydrationInProgress,
	reportHydrationEnd,
} from '@jira/ssr__hydrate-render-mode/src/useIsHydrateRenderMode.tsx';
import { autoBeforeEachTest } from '@jira/platform__utils-auto-before-each-test/src/auto-before-each-test.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
// eslint-disable-next-line jira/import/file-extension-required
import { callWhenIdle } from '@jira/platform__utils-call-when-idle';
// eslint-disable-next-line jira/restricted/@atlassian/react-sweet-state
import {
	createActionsHook,
	createStateHook,
	createStore,
	type Action,
} from '@atlassian/react-sweet-state';
import { fg } from '@atlassian/jira-feature-gating';
import log from '@atlassian/jira-common-util-logging/src/log.tsx';
import { callWhenIdleWithTimeout } from './callWhenIdleWithTimeout.tsx';

type DebugInfo = {
	pendingIds: Record<string, string>;
	firstPendingCallAt: number | null;
	lastCompletionCallAt: number | null;
	addPendingSubTreeCallCount: number;
	completeSubTreeCallCount: number;
	unorderlyCompleteSubtreeCalls: number;
	completionAttempts: number;
	idleCallbackTimedOut: boolean;
	globalTimedOut: boolean;
};
let DEBUG_INFO: DebugInfo = {
	pendingIds: {},
	firstPendingCallAt: null,
	lastCompletionCallAt: null,
	addPendingSubTreeCallCount: 0,
	completeSubTreeCallCount: 0,
	unorderlyCompleteSubtreeCalls: 0,
	completionAttempts: 0,
	idleCallbackTimedOut: false,
	globalTimedOut: false,
};
export const getPendingSubtreeNames = (): string[] => {
	return Object.values(DEBUG_INFO.pendingIds);
};
export const getIsGlobalTimedOut = (): boolean => DEBUG_INFO.globalTimedOut;

type Unsubscribe = () => void;
let clearNextCallback: Unsubscribe | null = null;
const setOrReplaceNextCallback = (createCallback: () => Unsubscribe) => {
	if (clearNextCallback != null) {
		clearNextCallback();
	}
	clearNextCallback = createCallback();
};
let clearGlobalTimeout: Unsubscribe | null = null;

autoBeforeEachTest(() => {
	DEBUG_INFO = {
		pendingIds: {},
		firstPendingCallAt: null,
		lastCompletionCallAt: null,
		addPendingSubTreeCallCount: 0,
		completeSubTreeCallCount: 0,
		unorderlyCompleteSubtreeCalls: 0,
		completionAttempts: 0,
		idleCallbackTimedOut: false,
		globalTimedOut: false,
	};

	clearNextCallback?.();
	clearNextCallback = null;
	clearGlobalTimeout?.();
	clearGlobalTimeout = null;
});

type OldStoreState = { isComplete: boolean; pendingIds: Record<string, string> };
const TIMEOUT_AFTER_LAST_SUBTREE_START = 5_000;
const OLD_GLOBAL_TIMEOUT = 30_000;
const GLOBAL_TIMEOUT = 60_000;
const HYDRATION_COMPLETE_WINDOW_EVENT = 'jira:hydration-complete';
const oldActions = {
	addPendingSubtree:
		(componentId: string, name: string): Action<OldStoreState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			DEBUG_INFO.addPendingSubTreeCallCount++;
			if (DEBUG_INFO.firstPendingCallAt == null) {
				DEBUG_INFO.firstPendingCallAt = Date.now();
			}
			if (clearGlobalTimeout == null) {
				const timerId = setTimeout(() => {
					DEBUG_INFO.globalTimedOut = true;
					dispatch(oldActions.complete());
				}, OLD_GLOBAL_TIMEOUT);
				clearGlobalTimeout = () => clearTimeout(timerId);
			}
			const previousPendingIds = getState().pendingIds;
			if (!Object.hasOwn(previousPendingIds, componentId)) {
				const updatedPendingIds = {
					...previousPendingIds,
					[componentId]: name,
				};
				setState({ pendingIds: updatedPendingIds });
				DEBUG_INFO.pendingIds = updatedPendingIds;
				// Fallback logic to end hydration 5s after the last pending subtree is added, if we get stuck
				setOrReplaceNextCallback(() => {
					const timerId = setTimeout(() => {
						dispatch(oldActions.complete());
					}, TIMEOUT_AFTER_LAST_SUBTREE_START);
					return () => clearTimeout(timerId);
				});
			}
		},
	completePendingSubtree:
		(componentId: string): Action<OldStoreState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			DEBUG_INFO.completeSubTreeCallCount++;
			DEBUG_INFO.lastCompletionCallAt = Date.now();
			const previousPendingIds = getState().pendingIds;
			if (!Object.hasOwn(previousPendingIds, componentId)) {
				DEBUG_INFO.unorderlyCompleteSubtreeCalls++;
			}
			const updatedPendingIds = { ...previousPendingIds };
			delete updatedPendingIds[componentId];
			setState({ pendingIds: updatedPendingIds });
			DEBUG_INFO.pendingIds = updatedPendingIds;
			if (Object.keys(updatedPendingIds).length === 0) {
				DEBUG_INFO.completionAttempts++;
				setOrReplaceNextCallback(() => {
					return callWhenIdleWithTimeout((deadline) => {
						if (deadline?.didTimeout) {
							DEBUG_INFO.idleCallbackTimedOut = true;
						}
						dispatch(oldActions.complete());
					});
				});
			}
		},
	complete:
		(): Action<OldStoreState> =>
		({ setState }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			reportHydrationEnd();
			setState({ isComplete: true });
			clearNextCallback?.();
			clearGlobalTimeout?.();
			if (globalThis.window) {
				globalThis.window.dispatchEvent(new CustomEvent(HYDRATION_COMPLETE_WINDOW_EVENT));
			}
			const {
				pendingIds,
				firstPendingCallAt,
				lastCompletionCallAt,
				addPendingSubTreeCallCount,
				completeSubTreeCallCount,
				unorderlyCompleteSubtreeCalls,
				completionAttempts,
				idleCallbackTimedOut,
				globalTimedOut,
			} = DEBUG_INFO;
			const pendingSubtreeNames = Object.values(pendingIds);
			if (
				(pendingSubtreeNames.length > 0 || globalTimedOut) &&
				fg('jfp-magma-log-dirty-hydration-completion')
			) {
				let totalHydrationDuration: number | null = null;
				if (firstPendingCallAt != null && lastCompletionCallAt != null) {
					totalHydrationDuration = lastCompletionCallAt - firstPendingCallAt;
				}
				log.safeErrorWithoutCustomerData(
					'hydration-completion',
					[
						'Failed to cleanly mark hydration as complete',
						`addPendingSubTreeCallCount=${addPendingSubTreeCallCount}`,
						`completeSubTreeCallCount=${completeSubTreeCallCount}`,
						`unorderlyCompleteSubtreeCalls=${unorderlyCompleteSubtreeCalls}`,
						`globalCompletionAttempts=${completionAttempts}`,
						`idleCallbackTimedOut=${idleCallbackTimedOut}`,
						`totalHydrationDuration=${totalHydrationDuration}`,
						`globalTimedOut=${globalTimedOut}`,
						`pendingSubtreeNames: [${pendingSubtreeNames.join(', ')}]`,
					].join(', '),
				);
			}
		},
};
const oldStore = createStore<OldStoreState, typeof oldActions>({
	name: 'hydration-global-completion',
	initialState: () => ({ isComplete: false, pendingIds: {} }),
	actions: oldActions,
});

let clearCompletionCallback: Unsubscribe | null = null;

autoBeforeEachTest(() => {
	clearCompletionCallback?.();
	clearCompletionCallback = null;
});

type StoreState = {
	isComplete: boolean;
	lastSubtreeChangeAt: number | null;
	pendingIds: Record<string, string>;
};

const actions = {
	addPendingSubtree:
		(componentId: string, name: string): Action<StoreState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			const previousPendingIds = getState().pendingIds;
			if (Object.hasOwn(previousPendingIds, componentId)) {
				return;
			}
			if (DEBUG_INFO.firstPendingCallAt == null) {
				DEBUG_INFO.firstPendingCallAt = Date.now();
			}
			if (clearGlobalTimeout == null) {
				const timerId = setTimeout(() => {
					DEBUG_INFO.globalTimedOut = true;
					dispatch(actions.complete());
				}, GLOBAL_TIMEOUT);
				clearGlobalTimeout = () => clearTimeout(timerId);
			}
			DEBUG_INFO.addPendingSubTreeCallCount++;
			clearCompletionCallback?.();
			const updatedPendingIds = {
				...previousPendingIds,
				[componentId]: name,
			};
			setState({ pendingIds: updatedPendingIds, lastSubtreeChangeAt: Date.now() });
			DEBUG_INFO.pendingIds = updatedPendingIds;
		},
	completePendingSubtree:
		(componentId: string): Action<StoreState> =>
		({ getState, setState, dispatch }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			const previousPendingIds = getState().pendingIds;
			if (!Object.hasOwn(previousPendingIds, componentId)) {
				return;
			}
			DEBUG_INFO.completeSubTreeCallCount++;
			const calledAt = Date.now();
			DEBUG_INFO.lastCompletionCallAt = calledAt;
			const updatedPendingIds = { ...previousPendingIds };
			delete updatedPendingIds[componentId];
			setState({ pendingIds: updatedPendingIds, lastSubtreeChangeAt: calledAt });
			DEBUG_INFO.pendingIds = updatedPendingIds;
			if (Object.keys(updatedPendingIds).length === 0) {
				const scheduleCompletionCallback = (remainingDeferrals: number) => {
					if (remainingDeferrals <= 0) {
						dispatch(actions.complete());
						clearCompletionCallback = null;
						return;
					}
					clearCompletionCallback = callWhenIdle(() => {
						const timerId = setTimeout(() => {
							scheduleCompletionCallback(remainingDeferrals - 1);
						}, 1_000);
						clearCompletionCallback = () => clearTimeout(timerId);
					});
				};
				clearCompletionCallback?.();
				scheduleCompletionCallback(5);
			}
		},
	complete:
		(): Action<StoreState> =>
		({ setState }) => {
			if (!getIsHydrationInProgress()) {
				return;
			}
			reportHydrationEnd();
			setState({ isComplete: true });
			clearGlobalTimeout?.();
			clearCompletionCallback?.();
			if (globalThis.window) {
				globalThis.window.dispatchEvent(new CustomEvent(HYDRATION_COMPLETE_WINDOW_EVENT));
			}
			const {
				pendingIds,
				firstPendingCallAt,
				lastCompletionCallAt,
				addPendingSubTreeCallCount,
				completeSubTreeCallCount,
				globalTimedOut,
			} = DEBUG_INFO;
			const pendingSubtreeNames = Object.values(pendingIds);
			if (
				(pendingSubtreeNames.length > 0 || globalTimedOut) &&
				fg('jfp-magma-log-dirty-hydration-completion')
			) {
				let totalHydrationDuration: number | null = null;
				if (firstPendingCallAt != null && lastCompletionCallAt != null) {
					totalHydrationDuration = lastCompletionCallAt - firstPendingCallAt;
				}
				log.safeErrorWithoutCustomerData(
					'hydration-completion',
					[
						'Failed to cleanly mark hydration as complete',
						`addPendingSubTreeCallCount=${addPendingSubTreeCallCount}`,
						`completeSubTreeCallCount=${completeSubTreeCallCount}`,
						`totalHydrationDuration=${totalHydrationDuration}`,
						`globalTimedOut=${globalTimedOut}`,
						`pendingSubtreeNames: [${pendingSubtreeNames.join(', ')}]`,
					].join(', '),
				);
			}
		},
};
/**
 * We need access to global hydration state inside React (reactive) and outside React, so have both a store and module state.
 */
const store = createStore<StoreState, typeof actions>({
	name: 'hydration-global-completion',
	initialState: () => ({ isComplete: false, lastSubtreeChangeAt: null, pendingIds: {} }),
	actions,
});

/**
 * Indicates if hydration has completed globally - all subtrees have completed hydrating.
 * NB: this only makes sense to request if {@link useIsHydrateRenderMode} is true.
 */
export const useIsGlobalHydrationComplete: () => boolean = functionWithCondition(
	() => fg('jfp-magma-hydration-completion-fix-timing'),
	createStateHook(store, {
		selector: ({ isComplete }) => isComplete,
	}),
	createStateHook(oldStore, {
		selector: ({ isComplete }) => isComplete,
	}),
);

export const useGlobalHydrationActions: () => {
	addPendingSubtree: (componentId: string, name: string) => void;
	completePendingSubtree: (componentId: string) => void;
} = functionWithCondition(
	() => fg('jfp-magma-hydration-completion-fix-timing'),
	createActionsHook(store),
	createActionsHook(oldStore),
);
